import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import ViewUnitsLanguagesModal from './ViewUnitsLanguagesModal'
import AddEditUnitsLanguagesModal from './AddEditUnitsLanguagesModal'
import FiltersModal, { FilterType } from '../../common/FiltersModal'
import SearchWithFilter from '../../common/SearchWithFilter'
import EmptyPageResults from '../../common/EmptyPageResults'
import LoadingSpinner from '../../common/LoadingSpinner'
import Dropdown from '../../common/Dropdown'
import SimpleButton from '../../common/SimpleButton'
import GenericTable from '../../common/GenericTable'
import ActionsMenu from '../../common/ActionsMenu'
import { toast } from '../../common/Toast'
import ConfirmationModal from '../../common/ConfirmationModal'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon, DownloadIcon, InformationIcon } from '../../../icons'
import { unitsLanguagesDeletePermissions, unitsLanguagesWritePermissions } from '../../../utils/permissionValidation'
import { downloadFileUTF8 } from '../../../utils/functions'
import { unitLanguagesUploadInstructions } from '../../../utils/fixedUrls'
import {
  loadUnitsLanguages,
  addUnitsLanguage,
  updateUnitsLanguage,
  removeUnitsLanguage,
  clearUnitsLanguagesMessages,
  loadUnitsLanguagesLocales,
  loadUnitsLanguagesProductTypes,
  downloadUnitLanguages,
} from '../../../actions/unitsLanguages'
import { useWindowSize } from '../../../libs/hooks'
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect'
import '../uploads.scss'

const orderingFields = [
  {
    field: 'productType',
    order: 'asc',
    translationKey: 'product_type_asc',
  },
  {
    field: 'productType',
    order: 'desc',
    translationKey: 'product_type_desc',
  },
  {
    field: 'languageId',
    order: 'asc',
    translationKey: 'language_id_asc',
  },
  {
    field: 'languageId',
    order: 'desc',
    translationKey: 'language_id_desc',
  },
  {
    field: 'englishText',
    order: 'asc',
    translationKey: 'english_text_asc',
  },
  {
    field: 'englishText',
    order: 'desc',
    translationKey: 'english_text_desc',
  },
]

const UnitsLanguages = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [unitsLanguagesTableData, setUnitsLanguagesTableData] = useState([])
  const [selectedFilterProductType, setSelectedFilterProductType] = useState(null)
  const [selectedFilterLocale, setSelectedFilterLocale] = useState(null)
  const [selectedLangDetails, setSelectedLangDetails] = useState(null)
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [expandedLang, setExpandedLang] = useState(0)
  const [showAddEditUnitsLanguagesModal, setShowAddEditUnitsLanguagesModal] = useState(false)
  const [showViewUnitsLanguagesModal, setShowViewUnitsLanguagesModal] = useState(false)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [langIdToDelete, setLangIdToDelete] = useState(null)
  const hasTable = unitsLanguagesTableData.length > 0
  useEffect(() => {
    if (props.unitsLanguagesLocales.length === 0) {
      props.actions.loadUnitsLanguagesLocales()
    }

    if (props.unitsLanguagesProductTypes.length === 0) {
      props.actions.loadUnitsLanguagesProductTypes()
    }
  }, [props.actions])

  useEffect(() => {
    const tableData = props.unitsLanguagesData.map((item, index) => {
      const en = (item.unitLanguages || []).find((l) => l.languageCode === 'EN') || item.unitLanguages?.[0] || {}

      return {
        index,
        identifier: item.id,
        productType: item.productType?.toUpperCase(),
        languageId: item.unitLanguageId,
        englishText: en.text || '',
        languageCode: en.languageCode || '',
        locale: en.locale || '',
        unitLanguages: item.unitLanguages,
        isCycleName: item.isCycleName,
        isEventName: item.isEventName,
        actions: [
          {
            label: 'view_details',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEdit(item.id)
            },
          },
          {
            label: 'edit',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEdit(item.id, true)
            },
            requiredPermission: unitsLanguagesWritePermissions,
          },
          {
            label: 'delete',
            onActionClick: (e) => {
              e.stopPropagation()
              showConfirmationModal(item.id)
            },
            requiredPermission: unitsLanguagesDeletePermissions,
          },
        ],
      }
    })

    setUnitsLanguagesTableData(tableData)
  }, [props.unitsLanguagesData])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearUnitsLanguagesMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearUnitsLanguagesMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadTableData(false)
    }
  }, [selectedPage])

  useEffect(() => {
    if (!showViewUnitsLanguagesModal || !selectedLangDetails) return
    const next = (props.unitsLanguagesData || []).find((x) => x.id === selectedLangDetails.id)
    if (next && next !== selectedLangDetails) setSelectedLangDetails(next)
  }, [props.unitsLanguagesData, showViewUnitsLanguagesModal, selectedLangDetails?.id])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadTableData(true)
        }

        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, selectedFilterProductType, selectedFilterLocale, orderBy])

  const handleDownload = () => {
    let queryParams = createQueryParams()
    queryParams = {
      ...queryParams,
    }
    props.actions.downloadUnitLanguages(queryParams).then((res) => {
      const date = new Date().toISOString().split('T')
      const dateNow = date[0].split('-').reverse().join('_')
      const timeNow = date[1].split('.')[0].split(':').join('_')
      downloadFileUTF8({
        data: res.value,
        fileName: `units_languages_${dateNow}_${timeNow}.csv`,
        fileType: 'text/csv',
      })
    })
  }
  const handleLoadTableData = (withReset) => {
    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      startPage: withReset ? 1 : selectedPage,
    }

    props.actions.loadUnitsLanguages(queryParams, withReset).then(() => setIsFirstLoad(false))
  }

  const handleSearch = (value) => {
    if (value?.length > 1) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setSelectedFilterProductType(null)
    setSelectedFilterLocale(null)
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (selectedFilterProductType) {
      queryParams = { ...queryParams, productType: selectedFilterProductType }
    }

    if (selectedFilterLocale) {
      queryParams = { ...queryParams, locale: selectedFilterLocale }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const handleOpenViewEdit = (langId, isEditModal) => {
    const fromList = (props.unitsLanguagesData || []).find((x) => x.id === langId)
    const freshest = props.langDetails && props.langDetails.id === langId ? props.langDetails : fromList

    if (!freshest) return
    setSelectedLangDetails(freshest)

    isEditModal ? setShowAddEditUnitsLanguagesModal(true) : setShowViewUnitsLanguagesModal(true)
  }

  const showConfirmationModal = (id) => {
    setLangIdToDelete(id)
    setShowDeleteConfirmModal(true)
  }

  const handleDeleteLang = () => {
    setShowDeleteConfirmModal(false)
    props.actions.removeUnitsLanguage(langIdToDelete)
  }

  const closeModal = () => {
    props.actions.clearUnitsLanguagesMessages()
    setSelectedLangDetails(null)
    setShowAddEditUnitsLanguagesModal(false)
    setShowViewUnitsLanguagesModal(false)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  return (
    <Fragment>
      <div className="uploads-wrapper">
        <div className="actions-wrapper flex-justify-between">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={selectedFilterProductType || selectedFilterLocale}
            counterBadge={(selectedFilterProductType && 1) + (selectedFilterLocale && 1)}
            minSearchLength={2}
          />
          <div className="unit-languages-download">
            <SimpleButton className="action-button" onClick={() => handleDownload()} disabled={props.isLoading || !hasTable}>
              <DownloadIcon />
              {t('download')}
            </SimpleButton>
            <SimpleButton className="submit-button" onClick={() => setShowAddEditUnitsLanguagesModal(true)} requiredPermission={unitsLanguagesWritePermissions}>
              {t('units_languages_context.add_unit_language')}
            </SimpleButton>
          </div>
        </div>
        <div className="information-box-text">
          <div className="icon-wrapper">
            <InformationIcon />
          </div>
          <div className="information-wrapper">
            <div className="title">
              {`${t('units_languages_context.upload_instructions')} `}
              <span>
                <a href={unitLanguagesUploadInstructions} target="_blank" rel="noreferrer" className="external-link">
                  {t('document_context.click_here')}
                </a>
              </span>
            </div>
          </div>
        </div>
        {isMobile && unitsLanguagesTableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}
        {!isMobile && unitsLanguagesTableData.length > 0 && (
          <GenericTable
            data={unitsLanguagesTableData}
            headers={[
              {
                title: t('product_type'),
                orderKey: 'productType',
              },
              {
                title: t('units_languages_context.language_id'),
                orderKey: 'languageId',
              },
              {
                title: t('units_languages_context.english_text'),
                orderKey: 'englishText',
              },
              {
                title: t('units_languages_context.locale'),
              },
              {
                title: '',
              },
            ]}
            keys={['productType', 'languageId', 'englishText', 'locale', 'action']}
            keyRenderer={{
              action: (item) => {
                return (
                  <div
                    className="table-actions-wrapper"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleShowActionsMenu(item['index'])
                    }}
                  >
                    {showActionsMenu === item['index'] ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === item['index'] && (
                      <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                    )}
                  </div>
                )
              },
            }}
            onRowClick={(lang) => handleOpenViewEdit(lang.identifier)}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}
        {isMobile &&
          unitsLanguagesTableData.map((lang, index) => (
            <div key={index} className="card" onClick={() => setExpandedLang(index)}>
              <div className={'card-item' + (expandedLang !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('product_type')}</div>
                <div className={'card-item-body' + (expandedLang !== index ? ' align-center' : '')}>
                  <div>{lang.productType}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={lang.actions} isLoading={showSpinner} />}
                  </div>
                </div>
              </div>
              {expandedLang === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('units_languages_context.language_id')}</div>
                    <div className="card-item-body">{lang.languageId}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('units_languages_context.english_text')}</div>
                    <div className="card-item-body">{lang.englishText}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('units_languages_context.locale')}</div>
                    <div className="card-item-body">{lang.locale}</div>
                  </div>
                </>
              )}
            </div>
          ))}
        {!showSpinner && !isFirstLoad && unitsLanguagesTableData.length === 0 && (
          <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
        )}
        {canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('product_type'),
              type: FilterType.singleSelect,
              dataset: props.unitsLanguagesProductTypes,
              input: selectedFilterProductType,
              output: (filterType) => {
                setSelectedFilterProductType(filterType)
              },
            },
            {
              name: t('units_languages_context.locale'),
              type: FilterType.singleSelect,
              dataset: props.unitsLanguagesLocales.map((l) => l.locale),
              input: selectedFilterLocale,
              output: (loc) => {
                setSelectedFilterLocale(loc)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
      {showAddEditUnitsLanguagesModal && (
        <AddEditUnitsLanguagesModal
          langDetails={selectedLangDetails}
          locales={props.unitsLanguagesLocales}
          productTypes={props.unitsLanguagesProductTypes}
          closeModal={closeModal}
          showSpinner={showSpinner}
          setShowSpinner={setShowSpinner}
        />
      )}
      {showViewUnitsLanguagesModal && selectedLangDetails && (
        <ViewUnitsLanguagesModal
          langDetails={selectedLangDetails}
          locales={props.unitsLanguagesLocales}
          showSpinner={showSpinner}
          setShowSpinner={setShowSpinner}
          handleEditLang={() => {
            setShowViewUnitsLanguagesModal(false)
            setShowAddEditUnitsLanguagesModal(true)
          }}
          closeModal={closeModal}
        />
      )}
      {showDeleteConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteConfirmModal(false)}
          onAccept={() => handleDeleteLang()}
          onCancel={() => setShowDeleteConfirmModal(false)}
          message={t('units_languages_context.delete_language_confirmation')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={unitsLanguagesDeletePermissions}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ unitsLanguages }) {
  return {
    unitsLanguagesData: unitsLanguages?.unitsLanguages || [],
    langDetails: unitsLanguages?.langDetails || null,
    unitsLanguagesLocales: unitsLanguages?.unitsLanguagesLocales || [],
    isLoading: unitsLanguages?.isLoading,
    canLoadMore: unitsLanguages?.canLoadMore,
    successMessage: unitsLanguages?.successMessage,
    errorMessage: unitsLanguages?.errorMessage,
    unitsLanguagesProductTypes: unitsLanguages?.unitsLanguagesProductTypes || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadUnitsLanguagesLocales,
        loadUnitsLanguages,
        loadUnitsLanguagesProductTypes,
        addUnitsLanguage,
        updateUnitsLanguage,
        removeUnitsLanguage,
        downloadUnitLanguages,
        clearUnitsLanguagesMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(UnitsLanguages)

